import {
    SERVER_METRICS_MIN_WINDOW_SECONDS,
    SERVER_METRICS_MEM_QUERY,
    SERVER_METRICS_RANGE_SECONDS,
    ServerMetricsRangeKey,
} from 'features/server/lib/serverMetrics';

import dateFormatter from 'date-and-time';
import {t} from '@lingui/core/macro';

import {IServerMetricsSeries, ServerMetricName} from '../model/Server/IServerMetrics';

import {convertTZ} from '../../../shared/lib/datetime';
import {isTranslate} from '../../../shared/lib/lang';

export type ServerMetricsChartRow = {ts: number} & Record<string, number | null>;

const BYTES_IN_GB = 1024 ** 3;

export function isServerMetricsRangeKey(value: string | null): value is ServerMetricsRangeKey {
    return !!value && value in SERVER_METRICS_RANGE_SECONDS;
}

export function formatBytesToGb(bytes: number | null | undefined, emptyValue = '—'): string {
    if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) {
        return emptyValue;
    }

    const formatted = (bytes / BYTES_IN_GB).toLocaleString(
        isTranslate() ? 'en-US' : 'ru-RU',
        {maximumFractionDigits: 1},
    );

    return `${formatted} ${t`ГБ`}`;
}

export function toChartRows(
    data: IServerMetricsSeries | undefined,
    keys: ServerMetricName[],
): ServerMetricsChartRow[] {
    if (!data) {
        return [];
    }

    return data.ts.map((ts, index) => {
        const row = {ts} as ServerMetricsChartRow;
        keys.forEach((key) => {
            const series = data[key];
            row[key] = series ? series[index] ?? null : null;
        });
        return row;
    });
}

export function toFsChartRows(
    data: IServerMetricsSeries | undefined,
    mount: string | undefined,
): ServerMetricsChartRow[] {
    if (!data || !mount) {
        return [];
    }

    const fs = data.fs[mount];

    return data.ts.map((ts, index) => ({
        ts,
        used_pct: fs ? fs.used_pct[index] ?? null : null,
        inodes_pct: fs ? fs.inodes_pct[index] ?? null : null,
    }));
}

export function toMemoryUsageChartRows(data: IServerMetricsSeries | undefined): ServerMetricsChartRow[] {
    const rows = toChartRows(data, SERVER_METRICS_MEM_QUERY);

    return rows.map((row) => {
        const memTotal = row.mem_total;
        const memAvailable = row.mem_available;
        const swapTotal = row.swap_total;
        const swapUsed = row.swap_used;

        return {
            ts: row.ts,
            mem_used_pct: memTotal && memAvailable !== null
                ? ((memTotal - memAvailable) / memTotal) * 100
                : null,
            mem_used_bytes: memTotal && memAvailable !== null
                ? memTotal - memAvailable
                : null,
            swap_used_pct: swapTotal
                ? ((swapUsed ?? 0) / swapTotal) * 100
                : null,
            swap_used_bytes: swapTotal
                ? swapUsed ?? 0
                : null,
        };
    });
}

export type ServerMetricsTickGranularity = 'time' | 'date';

export function formatServerMetricsTick(
    ts: number,
    granularity: ServerMetricsTickGranularity,
    tzString?: string | null,
): string {
    const date = convertTZ(new Date(ts * 1000), tzString);

    if (granularity === 'time') {
        return dateFormatter.format(date, 'HH:mm');
    }

    return dateFormatter.format(date, isTranslate() ? 'MM-DD' : 'DD.MM');
}

export function validateServerMetricsRange(
    from: number,
    to: number,
    retentionDays: number,
    nowSeconds: number,
): string | null {
    if (from > to) {
        return t`Начало периода не может быть позже его конца`;
    }
    if (to > nowSeconds) {
        return t`Нельзя выбрать период в будущем`;
    }
    if (to - from < SERVER_METRICS_MIN_WINDOW_SECONDS) {
        return t`Минимальная длина периода — 5 минут`;
    }
    if (from < nowSeconds - retentionDays * 86400) {
        return t`Запрошенный период больше глубины хранения метрик на вашем тарифе (${retentionDays} дн.)`;
    }
    return null;
}
